import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import {
  BookOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  EyeOutlined,
  HeartOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  Tooltip as AntTooltip,
  Avatar,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Grid,
  message,
  Row,
  Spin,
  Statistic,
  Tag,
} from "antd";
import Router from "../..";
import test_img from "../../../images/32637972_7843794.jpg";
import { formatCnt } from "../../utils/formatCnt";
import { CATEGORY_MAPPING, CATEGORY_STYLES } from "../../utils/categories";
import { getNiceDomain, getNiceStep } from "../../utils/chartScale";
import isoWeek from "dayjs/plugin/isoWeek";
import dayjs from "dayjs";
import {
  useInfluencerDetail,
  useInfluencerDetailCached,
} from "../../hooks/useInfluencer";
import { useState } from "react";
import { FaRegQuestionCircle } from "react-icons/fa";

dayjs.extend(isoWeek);

const formatCategories = (categories) => {
  if (!categories || categories.length === 0) return [];
  if (typeof categories === "string") {
    return categories.split(",").map((cat) => cat.trim());
  }
  return categories;
};

const getFrequentHashtags = (videos = [], minCount = 2, limit = 6) => {
  const counts = new Map();

  videos.forEach((video) => {
    const hashtags = String(video?.hashtag_name || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean)
      .map((tag) => tag.replace(/^#/, "").toLowerCase());

    hashtags.forEach((tag) => {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    });
  });

  return [...counts.entries()]
    .filter(([, count]) => count >= minCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tag]) => `#${tag}`);
};

const calculateEngagementRate = (detail) => {
  const influencerInfo = detail?.influencer_info;
  const latestChannelData = detail?.snapshot?.[0];

  const followerCount =
    parseInt(
      influencerInfo?.follower_count ?? latestChannelData?.follower_count
    ) || 0;
  const avgLikes =
    parseFloat(
      influencerInfo?.average_likes ?? latestChannelData?.average_likes
    ) || 0;
  const avgComments =
    parseFloat(
      influencerInfo?.average_comments ?? latestChannelData?.average_comments
    ) || 0;
  const avgShares =
    parseFloat(
      influencerInfo?.average_shares ?? latestChannelData?.average_shares
    ) || 0;

  const totalEngagement = avgLikes + avgComments + avgShares;

  if (followerCount === 0) return 0;
  return ((totalEngagement / followerCount) * 100).toFixed(2);
};

// 날짜 형식 변환 함수
const formatWeek = (weekStr) => {
  if (!weekStr) return "";

  const match = weekStr.match(/(\d{4})-W(\d+)/);
  if (!match) return weekStr;

  const year = Number(match[1]);
  const week = Number(match[2]);

  return dayjs(`${year}-01-04`).isoWeek(week).startOf("isoWeek").format("M/D");
};

const formatMonthWeekLabel = (weekStr) => {
  if (!weekStr) return "-";

  const match = weekStr.match(/(\d{4})-W(\d{1,2})/);
  if (!match) return weekStr;

  const year = Number(match[1]);
  const isoWeek = Number(match[2]);

  const startOfWeek = dayjs(`${year}-01-04`)
    .isoWeek(isoWeek)
    .startOf("isoWeek");

  const month = startOfWeek.month() + 1;
  const firstDayOfMonth = startOfWeek.startOf("month");

  const weekOfMonth = startOfWeek.diff(firstDayOfMonth, "week") + 1;

  return `${month}월 ${weekOfMonth}주차`;
};

// 스냅샷 데이터를 차트용 데이터로 변환
const prepareSnapshotTrends = (snapshots) => {
  if (!snapshots || snapshots.length === 0)
    return {
      followers: [],
      likes: [],
      videos: [],
      avgViews: [],
      avgLikes: [],
      avgComments: [],
      avgCollects: [],
      avgShares: [],
    };

  const sorted = [...snapshots].sort((a, b) =>
    a.snapshot_week.localeCompare(b.snapshot_week)
  );

  return {
    followers: sorted
      .filter(
        (snap) =>
          snap.follower_count !== null && snap.follower_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.follower_count) || 0,
      })),
    likes: sorted
      .filter(
        (snap) => snap.like_count !== null && snap.like_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.like_count) || 0,
      })),
    videos: sorted
      .filter(
        (snap) => snap.video_count !== null && snap.video_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.video_count) || 0,
      })),
    avgViews: sorted
      .filter(
        (snap) =>
          snap.average_views !== null && snap.average_views !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_views) || 0,
      })),
    avgLikes: sorted
      .filter(
        (snap) =>
          snap.average_likes !== null && snap.average_likes !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_likes) || 0,
      })),
    avgComments: sorted
      .filter(
        (snap) =>
          snap.average_comments !== null && snap.average_comments !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_comments) || 0,
      })),
    avgCollects: sorted
      .filter(
        (snap) =>
          snap.average_collects !== null && snap.average_collects !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_collects) || 0,
      })),
    avgShares: sorted
      .filter(
        (snap) =>
          snap.average_shares !== null && snap.average_shares !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_shares) || 0,
      })),
  };
};

// 주차 계산
const calcWeek = (weekStr) => {
  if (!weekStr) return;

  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const dayOfYear =
    Math.floor(
      (now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)
    ) + 1;
  const startDay = startOfYear.getDay();
  let weekNumber = Math.ceil((dayOfYear + startDay - 1) / 7);
  if (startDay > 4) {
    weekNumber -= 1;
  }
  const week = `${now.getFullYear()}-W${weekNumber.toString().padStart(2, "0")}`;

  if (weekStr !== week) {
    return true;
  }
  return false;
};

export const InfluencerDetailDrawer = ({
  visible,
  onClose,
  influencer,
  onInvalidUser,
}) => {
  const [showAiAnalysisView, setShowAiAnalysisView] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  const {
    data: influencerDetail,
    refetch: refetchInfluencerDetail,
    isLoading,
  } = useInfluencerDetail({
    unique_id: influencer?.unique_id,
    enabled: false,
    onInvalidUser,
  });

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { data: influencerDetailCached, isLoading: isLoadingCached } =
    useInfluencerDetailCached({
      unique_id: influencer?.unique_id,
      enabled: visible && !!influencer?.unique_id,
      onInvalidUser,
    });

  const detail = influencerDetail ?? influencerDetailCached;
  const latestChannelData = detail?.snapshot?.[0] ?? {};
  const latestAiAnalysis = detail?.snapshot[0]?.ai_analysis?.data ?? null;

  const isValidAudience = (data) =>
    Array.isArray(data) && data.length > 0 && data[0] !== "null";

  const hasAudienceData =
    isValidAudience(detail?.influencer_info?.audience_ages) ||
    isValidAudience(detail?.influencer_info?.audience_genders) ||
    isValidAudience(detail?.influencer_info?.audience_countries) ||
    isValidAudience(detail?.influencer_info?.audience_cities);

  const isRegistered =
    detail?.influencer_info?.metrics !== null &&
    detail?.influencer_info?.metrics !== undefined;
  const influencerInfo = detail?.influencer_info;
  const snapshots = detail?.snapshot || [];
  const categories = detail?.category || [];

  // 추이 데이터 준비
  const trends = prepareSnapshotTrends(snapshots);

  // influencer_info의 recent_videos 사용
  const recentVideos = influencerInfo?.recent_videos || [];
  const repeatedHashtags = getFrequentHashtags(recentVideos);

  // 인게이지 오디언스, 최근 7일 앱 다운로드
  const metricsData = [...(detail?.influencer_info?.metrics || [])].sort(
    (a, b) => (a?.date || "").localeCompare(b?.date || "")
  );
  const engagedAudienceTrends = metricsData.map((item) => ({
    week: item?.date ? dayjs(item.date).format("M/D") : "",
    value: Number(item?.engaged_audience ?? 0),
  }));
  const appDownloadClickTrends = metricsData.map((item) => ({
    week: item?.date ? dayjs(item.date).format("M/D") : "",
    value: Number(item?.app_download_clicks ?? 0),
  }));

  // 통계 카드 컴포넌트 (recharts 사용)
  const TrendCard = ({ title, titleText, data, showAllTicks = false }) => {
    const validData = data.filter(
      (d) => d.week && d.week !== "undefined/undefined"
    );

    const values = validData.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);

    const [niceMin, niceMax] = getNiceDomain(min, max, 4);
    const yStep = getNiceStep(min, max, 4);

    const ticks = [];
    for (let v = niceMin; v <= niceMax; v += yStep) ticks.push(v);

    const resolvedTitleText =
      titleText || (typeof title === "string" ? title : "trend");
    const gradientId = `gradient-${resolvedTitleText.replace(
      /[^a-zA-Z0-9]/g,
      ""
    )}`;

    const MAX_X_TICKS = 5;
    const xTickLabels = validData.map((d) => d.week);

    const xStep = Math.ceil(xTickLabels.length / MAX_X_TICKS);

    const xTicks = showAllTicks
      ? xTickLabels
      : xTickLabels.filter((_, i) => i % xStep === 0);

    if (
      !showAllTicks &&
      xTicks.at(-1) !== xTickLabels.at(-1) &&
      xTickLabels.length > 0
    ) {
      xTicks.push(xTickLabels.at(-1));
    }

    return (
      <Card
        styles={{
          body: {
            padding: "20px",
            background: "#FAFAFA",
            borderRadius: "8px",
          },
        }}
        className="rounded-[8px] border border-[#E8E8E8]"
      >
        <div className="mb-4">
          <span className="text-[14px] font-semibold text-[#262626]">
            {title}
          </span>
        </div>
        {validData.length > 0 ? (
          <div className="audience-chart-wrap">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={validData}
                margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F9A43F" stopOpacity={0.35} />
                    <stop
                      offset="100%"
                      stopColor="#F9A43F"
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis
                  dataKey="week"
                  tick={{ fontSize: 11, fill: "#666" }}
                  axisLine={{ stroke: "#D9D9D9" }}
                  tickLine={{ stroke: "#D9D9D9" }}
                  ticks={xTicks}
                  tickMargin={6}
                  preserveStartEnd
                  padding={{ left: 0, right: 0 }}
                />
                <YAxis
                  domain={[niceMin, niceMax]}
                  ticks={ticks}
                  tick={{ fontSize: 11, fill: "#666" }}
                  tickFormatter={(value) => formatCnt(value)}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                />
                <RechartsTooltip
                  formatter={(value) => [formatCnt(value), resolvedTitleText]}
                  labelStyle={{ fontSize: 12, fontWeight: 600 }}
                  contentStyle={{
                    fontSize: 11,
                    borderRadius: "6px",
                    border: "1px solid #E8E8E8",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    padding: "8px 12px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#F9A43F"
                  strokeWidth={2.5}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Empty
            description="데이터 없음"
            styles={{
              root: { padding: "50px 0" },
              image: { height: 60 },
            }}
          />
        )}
      </Card>
    );
  };

  // 섹션 제목 컴포넌트
  const SectionTitle = ({ children }) => (
    <h3 className="mb-[20px] text-[18px] font-semibold text-[#262626] pb-[12px] border-b-[2px] border-[#F0F0F0]">
      {children}
    </h3>
  );

  const handleCampaignInvite = () => {
    if (!detail?.influencer_info?.email) {
      message.warning("이메일이 없는 크리에이터는 초대할 수 없습니다.");
      return;
    }
    setInviteModalVisible(true);
  };

  const handleInviteModalClose = () => {
    setInviteModalVisible(false);
  };

  const getAverageMetricTooltip = (label) =>
    `최근 10개 영상 기준 평균 ${label}입니다.`;

  const renderLabelWithTooltip = (label, tooltipText) => (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-[13px]">{label}</span>
      <AntTooltip title={tooltipText}>
        <span className="inline-flex items-center justify-center text-[#8C8C8C] transition-colors hover:text-[#595959]">
          <FaRegQuestionCircle className="text-[13px]" />
        </span>
      </AntTooltip>
    </div>
  );
  return (
    <Drawer
      title={
        <span className="text-[16px] font-semibold">
          {showAiAnalysisView ? "AI 분석" : "크리에이터 상세정보"}
        </span>
      }
      extra={
        !(isLoadingCached || isLoading) && influencerInfo ? (
          <div className="flex justify-end items-center gap-2">
            <Button
              type="primary"
              className={`bg-[#171717] hover:!bg-[#333333] text-white ${
                isMobile ? "text-lg" : "text-xl"
              }`}
              onClick={handleCampaignInvite}
              size={isMobile ? "small" : "middle"}
            >
              캠페인 초대
            </Button>

            <Button
              type="primary"
              onClick={() => setShowAiAnalysisView((v) => !v)}
              style={{ background: "#F9A43F", borderColor: "#F9A43F" }}
              className={isMobile ? "text-lg" : "text-xl"}
              size={isMobile ? "small" : "middle"}
            >
              {showAiAnalysisView ? "상세정보 보기" : "AI 분석 보기"}
            </Button>
          </div>
        ) : null
      }
      placement="right"
      onClose={() => {
        setShowAiAnalysisView(false);
        onClose();
      }}
      open={visible}
      width={1200}
      styles={{
        body: {
          padding: "24px",
          background: "#FFFFFF",
        },
      }}
      className="select-none"
    >
      {isLoadingCached || isLoading ? (
        <div className="flex flex-col items-center gap-6 py-[100px] text-center">
          <Spin size="large" />
          <div className="flex flex-col gap-2 text-[16px] text-[#666]">
            <div className="font-medium">
              최신 크리에이터 정보를 업데이트 및 불러오고 있습니다.
            </div>
            <div>잠시만 기다려주세요.</div>
          </div>
        </div>
      ) : influencerInfo ? (
        <>
          <Router.InfluencerAiAnalysis
            uniqueId={influencer?.unique_id}
            aiAnalysisData={latestAiAnalysis}
            isVisible={showAiAnalysisView}
          />
          {!showAiAnalysisView ? (
            <div>
              {/* 프로필 섹션 */}
              <Card className="mb-6 rounded-xl border border-[#E8E8E8]">
                <div className="flex flex-col gap-4 md:flex-row md:gap-12 md:items-center">
                  <Avatar
                    src={influencerInfo.profile_img || test_img}
                    className="!w-[72px] !h-[72px] md:!w-[92px] md:!h-[92px] border-4 border-[#F0F0F0]"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap justify-between items-center">
                      <div className="flex flex-wrap gap-1 ">
                        {formatCategories(categories).map((category, idx) => (
                          <Tag
                            key={idx}
                            className="border-none px-2 py-0.5 text-[11px] md:text-[12px] font-medium rounded-lg m-0"
                            style={
                              CATEGORY_STYLES[category] || CATEGORY_STYLES.etc
                            }
                          >
                            {CATEGORY_MAPPING[category] || category}
                          </Tag>
                        ))}
                      </div>

                      {calcWeek(detail?.snapshot[0]?.snapshot_week) ? (
                        <div>
                          <Button
                            type="primary"
                            style={{
                              background: "#F9A43F",
                              borderColor: "#F9A43F",
                            }}
                            className={`font-medium ${
                              isMobile ? "text-lg" : "text-xl"
                            }`}
                            onClick={() => refetchInfluencerDetail()}
                            loading={isLoading}
                            size={isMobile ? "small" : "middle"}
                          >
                            최신 정보 불러오기
                          </Button>
                        </div>
                      ) : (
                        <p className="text-[14px] text-[#8C8C8C]">
                          {formatMonthWeekLabel(
                            detail?.snapshot[0]?.snapshot_week
                          )}{" "}
                          기준 데이터입니다.
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <p className="m-0 text-[18px] md:text-[22px] font-bold leading-tight">
                        {influencerInfo.nickname || "-"}
                      </p>
                      {isRegistered && (
                        <CheckCircleOutlined className="text-[#52c41a] text-[16px] md:text-[18px]" />
                      )}
                    </div>

                    <p className="text-[#8C8C8C] m-0 text-[12px] md:text-[13px]">
                      @{influencerInfo.unique_id || "-"}
                    </p>

                    {repeatedHashtags.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {repeatedHashtags.map((hashtag) => (
                          <Tag
                            key={hashtag}
                            className="m-0 rounded-full border-none bg-[#F4F7FB] px-3 py-1 text-[11px] font-medium text-[#4B5563] md:text-[12px]"
                          >
                            {hashtag}
                          </Tag>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Card>

              {/* 기본 통계 */}
              <div className="mb-8">
                <Card
                  title={
                    <span className="text-[18px] font-semibold">채널 통계</span>
                  }
                  className="rounded-xl border border-[#E8E8E8]"
                  styles={{ body: { padding: "24px" } }}
                >
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    <Statistic
                      title={
                        <span className="text-[13px] text-[#8C8C8C]">
                          팔로워
                        </span>
                      }
                      value={influencerInfo.follower_count || 0}
                      prefix={
                        <UserOutlined className="text-[#5B8FF9] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={
                        <span className="text-[13px] text-[#8C8C8C]">
                          총 좋아요
                        </span>
                      }
                      value={influencerInfo.like_count || 0}
                      prefix={
                        <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={
                        <span className="text-[13px] text-[#8C8C8C]">
                          총 영상 수
                        </span>
                      }
                      value={influencerInfo.video_count || 0}
                      prefix={
                        <VideoCameraOutlined style={{ color: "#722ED1" }} />
                      }
                      valueStyle={{ fontSize: "24px", fontWeight: "600" }}
                    />
                    <Statistic
                      title={renderLabelWithTooltip(
                        "평균 조회수",
                        getAverageMetricTooltip("조회수")
                      )}
                      value={
                        influencerInfo.average_views ??
                        latestChannelData.average_views ??
                        0
                      }
                      prefix={
                        <EyeOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={renderLabelWithTooltip(
                        "평균 좋아요",
                        getAverageMetricTooltip("좋아요")
                      )}
                      value={
                        influencerInfo.average_likes ??
                        latestChannelData.average_likes ??
                        0
                      }
                      prefix={
                        <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={renderLabelWithTooltip(
                        "평균 저장",
                        getAverageMetricTooltip("저장")
                      )}
                      value={
                        influencerInfo.average_collects ??
                        latestChannelData.average_collects ??
                        0
                      }
                      prefix={
                        <BookOutlined className="text-[#FAAD14] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={renderLabelWithTooltip(
                        "평균 댓글 수",
                        getAverageMetricTooltip("댓글 수")
                      )}
                      value={
                        influencerInfo.average_comments ??
                        latestChannelData.average_comments ??
                        0
                      }
                      prefix={
                        <CommentOutlined className="text-[#1890FF] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={renderLabelWithTooltip(
                        "평균 공유 수",
                        getAverageMetricTooltip("공유 수")
                      )}
                      value={
                        influencerInfo.average_shares ??
                        latestChannelData.average_shares ??
                        0
                      }
                      prefix={
                        <ShareAltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold">
                          {formatCnt(value)}
                        </span>
                      )}
                    />
                    <Statistic
                      title={
                        <span className="text-[13px] text-[#8C8C8C]">ERF</span>
                      }
                      value={calculateEngagementRate(detail)}
                      prefix={
                        <ThunderboltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                      }
                      formatter={(value) => (
                        <span className="text-[22px] md:text-[24px] font-semibold text-[#52C41A]">
                          {value}%
                        </span>
                      )}
                    />
                  </div>
                </Card>
              </div>

              {/* 주간 추이 */}
              <div className="mb-8">
                <SectionTitle>주간 추이</SectionTitle>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard title="팔로워" data={trends.followers} />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard title="좋아요" data={trends.likes} />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard title="영상 수" data={trends.videos} />
                  </Col>
                </Row>
              </div>

              {/* 영상 인게이지먼트 추이 */}
              <div className="mb-8">
                <SectionTitle>영상 인게이지먼트 추이</SectionTitle>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard
                      title={renderLabelWithTooltip(
                        "평균 조회수",
                        getAverageMetricTooltip("조회수")
                      )}
                      titleText="평균 조회수"
                      data={trends.avgViews}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard
                      title={renderLabelWithTooltip(
                        "평균 좋아요",
                        getAverageMetricTooltip("좋아요")
                      )}
                      titleText="평균 좋아요"
                      data={trends.avgLikes}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard
                      title={renderLabelWithTooltip(
                        "평균 댓글",
                        getAverageMetricTooltip("댓글")
                      )}
                      titleText="평균 댓글"
                      data={trends.avgComments}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard
                      title={renderLabelWithTooltip(
                        "평균 저장",
                        getAverageMetricTooltip("저장")
                      )}
                      titleText="평균 저장"
                      data={trends.avgCollects}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <TrendCard
                      title={renderLabelWithTooltip(
                        "평균 공유",
                        getAverageMetricTooltip("공유")
                      )}
                      titleText="평균 공유"
                      data={trends.avgShares}
                    />
                  </Col>
                </Row>
              </div>

              {metricsData.length > 0 && (
                <div className="mb-8">
                  <SectionTitle>틱톡 사용자 반응 추이</SectionTitle>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <TrendCard
                        title="링크 클릭 수"
                        data={engagedAudienceTrends}
                        showAllTicks
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <TrendCard
                        title="유효 사용자 수"
                        data={appDownloadClickTrends}
                        showAllTicks
                      />
                    </Col>
                  </Row>
                </div>
              )}

              {hasAudienceData && (
                <div className="mb-8">
                  <SectionTitle>오디언스 정보</SectionTitle>
                  <Router.AudienceInsights
                    influencerInfo={detail.influencer_info}
                  />
                </div>
              )}

              {/* 최근 영상 */}
              {recentVideos && recentVideos.length > 0 && (
                <div className="mb-8">
                  <Router.RecentVideosCarousel
                    videos={recentVideos}
                    influencerId={influencerInfo.unique_id}
                  />
                </div>
              )}
            </div>
          ) : null}
        </>
      ) : (
        <Empty
          description="데이터를 불러올 수 없습니다"
          styles={{
            root: { padding: "100px 0" },
            image: { height: 100 },
          }}
        />
      )}
      <Router.InviteCampaign
        visible={inviteModalVisible}
        onClose={handleInviteModalClose}
        selectedInfluencers={
          detail?.influencer_info ? [detail?.influencer_info] : []
        }
        mode="single"
      />
    </Drawer>
  );
};
